import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    getSupportedLanguage,
    useRegional,
} from "../Regional/RegionalContext";

const TranslationContext = createContext(null);

export const TranslationProvider = ({ children }) => {
    const {
        language: regionalLanguage,
        setLanguage: setRegionalLanguage,
    } = useRegional();

    const language = getSupportedLanguage(regionalLanguage);
    
    const [dictionary, setDictionary] = useState({});

    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
                const dict = await import(`./Languages/${language}.js`);

                if (mounted) {
                    setDictionary(dict.default || dict);
                }
            } catch {
                const fallback = await import("./Languages/en.js");

                if (mounted) {
                    setDictionary(fallback.default || fallback);
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, [language]);

    const setLanguage = useCallback(
        (nextLanguage) => {
            setRegionalLanguage(getSupportedLanguage(nextLanguage));
        },
        [setRegionalLanguage]
    );

    const translate = useCallback(
        (key) => dictionary[key] || key,
        [dictionary]
    );

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            translate,
            dictionary,
        }),
        [language, setLanguage, translate, dictionary]
    );

    return (
        <TranslationContext.Provider value={value}>
            {children}
        </TranslationContext.Provider>
    );
};

export const useTranslate = () => {
    const context = useContext(TranslationContext);

    if (!context) {
        throw new Error("useTranslate must be used inside TranslationProvider");
    }

    return context;
};